import type { InspectionLifecycleState, InspectionTripType } from 'types/modules/inspections/inspection'

export const BLOCKING_STATES: InspectionLifecycleState[] = ['awaiting-repair', 'awaiting-approval']

export const LIFECYCLE_STATE_LABELS: Record<InspectionLifecycleState, string> = {
  open: 'No defects',
  'awaiting-review': 'Awaiting review',
  'awaiting-repair': 'Awaiting repair',
  'awaiting-approval': 'Awaiting confirmation',
  released: 'Signed',
}

// An open report can still carry defective items that are not out of service; those never block.
export const OPEN_WITH_DEFECTS_LABEL = 'Defects noted'

export const ITEM_STATE_LABELS = {
  backInService: 'Back in service',
  awaitingApproval: 'Awaiting approval',
}

export const ENTRY_VERDICT_LABELS = {
  comment: 'Additional comment',
  approved: 'Repairs approved',
  sentBack: 'Sent back',
}

export type InspectionStateFilter = 'all' | 'blocking' | InspectionLifecycleState

export const STATE_FILTER_OPTIONS: { value: InspectionStateFilter; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'blocking', label: 'Out of Service' },
  { value: 'awaiting-review', label: 'Awaiting review' },
  { value: 'awaiting-repair', label: 'Awaiting repair' },
  { value: 'awaiting-approval', label: 'Awaiting confirmation' },
  { value: 'released', label: 'Signed' },
  { value: 'open', label: 'No defects' },
]

export type InspectionTripFilter = 'all' | InspectionTripType

export const TRIP_FILTER_OPTIONS: { value: InspectionTripFilter; label: string }[] = [
  { value: 'all', label: 'Pre & post' },
  { value: 'pre-trip', label: 'Pre-trip' },
  { value: 'post-trip', label: 'Post-trip' },
]

export const TRIP_TYPE_LABELS: Record<InspectionTripType, string> = {
  'pre-trip': 'Pre-trip',
  'post-trip': 'Post-trip',
}

// The tablet's body-damage palette and the condition it asks for once a defect is placed.
export const ZONE_DAMAGE_TYPES = ['hole', 'broken', 'missing', 'scratch', 'cut', 'dent', 'patched']
export const ZONE_CONDITIONS = ['broken', 'cracked', 'dented', 'leaking', 'other']

// The tablet's tire issue slugs, in its chip order.
export const TIRE_ISSUE_LABELS: Record<string, string> = {
  flat: 'Flat',
  'air-low': 'Air Low',
  'tread-depth-low': 'Tread Depth Low',
  'loose-lug-nuts': 'Loose Lug Nuts',
  'crack-or-cut': 'Crack or Cut',
  'grease-leak': 'Grease Leak',
}

export interface TireAxle {
  left: number[]
  right: number[]
  startsNewGroup?: boolean
}

// Template v1's tire positions, each side listed left to right on screen; the break before the third axle splits tractor from trailer.
export const TIRE_AXLES: TireAxle[] = [
  { left: [1], right: [2] },
  { left: [3, 4], right: [5, 6] },
  { left: [7, 8], right: [9, 10], startsNewGroup: true },
  { left: [11, 12], right: [13, 14] },
]

export const ZONE_LABELS: Record<string, string> = {
  'street-side': 'Street side',
  'curb-side': 'Curb side',
  front: 'Front',
  back: 'Back',
  top: 'Top',
}

// Template v1, in the tablet's order (DriverApp docs/plans/inspections-v2-firestore-doc-proposal.md).
export const CHECKLIST_SECTIONS: { key: string; label: string }[] = [
  { key: 'general-condition', label: 'General Condition' },
  { key: 'engine-compartment', label: 'Engine Compartment' },
  { key: 'interior-cab', label: 'Interior Cab' },
  { key: 'exterior', label: 'Exterior' },
  { key: 'body-hydraulics', label: 'Body — Hydraulic' },
  { key: 'body-pump-leaks', label: 'Body — Pump Leaks' },
  { key: 'body-mechanical', label: 'Body — Mechanical' },
]

export const CHECKLIST_ITEM_LABELS: Record<string, string> = {
  'general-condition/cab-doors-and-windows': 'Cab Doors and Windows',
  'general-condition/body-doors': 'Body Doors',
  'general-condition/oil-leak': 'Oil Leak',
  'general-condition/grease-leak': 'Grease Leak',
  'general-condition/coolant-leak': 'Coolant Leak',
  'general-condition/fuel-leak': 'Fuel Leak',
  'engine-compartment/oil-level': 'Oil Level',
  'engine-compartment/coolant-level': 'Coolant Level',
  'engine-compartment/belts': 'Belts',
  'interior-cab/warning-indicators': 'Warning Indicators',
  'interior-cab/windshield-wipers': 'Windshield Wipers',
  'interior-cab/horns': 'Horns',
  'interior-cab/heater-defroster': 'Heater / Defroster',
  'interior-cab/mirrors': 'Mirrors',
  'interior-cab/steering': 'Steering',
  'interior-cab/clutch': 'Clutch',
  'interior-cab/service-brakes': 'Service Brakes',
  'interior-cab/parking-brakes': 'Parking Brakes',
  'interior-cab/emergency-brakes': 'Emergency Brakes',
  'interior-cab/triangles-flares': 'Triangles / Flares',
  'interior-cab/fire-extinguisher': 'Fire Extinguisher',
  'interior-cab/other-safety-equipment': 'Other Safety Equipment',
  'interior-cab/spare-fuses': 'Spare Fuses',
  'interior-cab/seat-belts': 'Seat Belts',
  'exterior/lights': 'Lights',
  'exterior/reflectors': 'Reflectors',
  'exterior/suspension': 'Suspension',
  'exterior/tires': 'Tires',
  'exterior/wheels-rims-lugs': 'Wheels, Rims & Lugs',
  'exterior/battery': 'Battery',
  'exterior/exhaust': 'Exhaust',
  'exterior/brakes': 'Brakes',
  'exterior/air-lines': 'Air Lines',
  'exterior/light-line': 'Light Line',
  'exterior/fifth-wheel': 'Fifth Wheel',
  'exterior/other-coupling': 'Other Coupling',
  'exterior/tie-downs': 'Tie Downs',
  'exterior/end-protection': 'End Protection',
  'body-hydraulics/street-side-cylinder': 'Street Side Cylinder',
  'body-hydraulics/street-side-line': 'Street Side Line',
  'body-hydraulics/street-side-valve': 'Street Side Valve',
  'body-hydraulics/curb-side-cylinder': 'Curb Side Cylinder',
  'body-hydraulics/curb-side-line': 'Curb Side Line',
  'body-hydraulics/curb-side-valve': 'Curb Side Valve',
  'body-hydraulics/front-cylinder': 'Front Cylinder',
  'body-hydraulics/front-line': 'Front Line',
  'body-hydraulics/front-valve': 'Front Valve',
  'body-hydraulics/back-cylinder': 'Back Cylinder',
  'body-hydraulics/back-line': 'Back Line',
  'body-hydraulics/back-valve': 'Back Valve',
  'body-hydraulics/top-door-cylinder': 'Top Door Cylinder',
  'body-hydraulics/top-door-line': 'Top Door Line',
  'body-hydraulics/top-door-valve': 'Top Door Valve',
  'body-pump-leaks/pto-leaks': 'PTO Leaks',
  'body-pump-leaks/check-fluid': 'Check Fluid',
  'body-mechanical/wont-pack-correctly': 'Won’t Pack Correctly',
  'body-mechanical/broken-or-missing-pins': 'Broken or Missing Pins',
}
